import { useCallback, useMemo } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import { UTCDate } from '@date-fns/utc';
import { addHours, subDays } from 'date-fns';
import { type AreaChartDatum, AreaChart } from '@/shared/components/charts/AreaChart';
import { getLendingLiquiditySnapshotsQuery } from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import TrendingDown from '@/shared/icons/flip-ui-kit/small/TrendingDown';
import TrendingUp from '@/shared/icons/flip-ui-kit/small/TrendingUp';
import { getDateAxisTicks } from '@/shared/utils/charts';
import { formatUsdValue } from '@/shared/utils/numbers';
import { SkeletonLine } from '../atoms/LoadingSkeleton';
import { FancyDollarDisplay } from '../FancyDollarDisplay';
import QuestionMarkTooltip from '../QuestionMarkTooltip';

type ChartDatum = {
  valueUsd: string;
  date: string;
};

const EmptyPlaceholder = () => (
  <div className="relative z-0 flex h-full w-full flex-col items-center justify-center rounded-md bg-cf-gray-3 font-aeonikMedium">
    <div className="flex flex-col items-center justify-center gap-y-6">
      <div className="flex flex-col items-center justify-center gap-y-3">
        <div className="text-24 text-cf-white">No historical data yet</div>
      </div>
    </div>
    <img
      src="/bar-chart-placeholder.png"
      alt="Bar Chart Placeholder"
      className="absolute bottom-0 -z-10 block h-[150px] w-full object-cover"
    />
  </div>
);

const AreaLineChart = ({
  isLoading,
  chartData,
  isDaily,
}: {
  chartData: ChartDatum[];
  isLoading: boolean;
  isDaily: boolean;
}) => {
  const sortedData = useMemo(
    () => [...chartData].sort((a, b) => (b.date > a.date ? -1 : 1)),
    [chartData],
  );

  const { areaData, xTickValues } = useMemo(() => {
    const data: AreaChartDatum[] = sortedData.map((item) => ({
      x: new Date(item.date),
      y: Number(item.valueUsd ?? 0),
    }));

    return {
      areaData: data,
      xTickValues: getDateAxisTicks(
        data.map((point) => point.x),
        10,
      ),
    };
  }, [sortedData]);

  const xTickFormat = useCallback(
    (dateString: number | Date) => {
      const date = new UTCDate(dateString);
      if (isDaily) {
        return date.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: 'numeric',
          timeZone: 'UTC',
        });
      }
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    },
    [isDaily],
  );

  const yTickFormat = useCallback(
    (value: string | number): string => String(formatUsdValue(value, false)),
    [],
  );

  return (
    <AreaChart
      className="-mx-4 h-[300px]"
      isLoading={isLoading}
      data={areaData}
      color="#46DA93"
      margin={{ top: 20, right: 40, bottom: 50, left: 60 }}
      xTickValues={xTickValues}
      xTickFormat={xTickFormat}
      yTickFormat={yTickFormat}
      renderTooltip={(datum: AreaChartDatum) => (
        <div className="w-[306px] space-y-1">
          <div className="flex flex-col items-start">
            <div className="text-12 text-cf-light-2">
              {isDaily
                ? new Date(datum.x).toLocaleString(undefined, {
                    timeZone: 'UTC',
                  })
                : new Date(datum.x).toLocaleDateString(undefined, {
                    timeZone: 'UTC',
                  })}{' '}
              UTC
            </div>
            <div className="text-20 text-cf-white">{formatUsdValue(datum.y)}</div>
          </div>
        </div>
      )}
    />
  );
};

export const PositionsValueChart = ({
  idSs58,
  days,
  currentSupplied,
}: {
  idSs58: string;
  days: number;
  currentSupplied: number;
}) => {
  const isDaily = days === 1;
  const { startDate, endDate, last24hStartDate, last24hEndDate } = useMemo(
    () => ({
      startDate: subDays(new Date(), days),
      endDate: new Date(),
      last24hStartDate: subDays(new Date(), 1),
      last24hEndDate: addHours(subDays(new Date(), 1), 1),
    }),
    [days],
  );
  const { data, isLoading } = useGqlQuery(getLendingLiquiditySnapshotsQuery, {
    context: { clientName: 'lpProcessor' },
    variables: {
      idSs58,
      minTimestamp: startDate.toISOString(),
      maxTimestamp: endDate.toISOString(),
      groupBy: isDaily ? 'TIMESTAMP_TRUNCATED_TO_HOUR' : 'TIMESTAMP_TRUNCATED_TO_DAY',
    },
    refetchInterval: 30_000,
  });

  const { data: last24hSnapshot } = useGqlQuery(getLendingLiquiditySnapshotsQuery, {
    context: { clientName: 'lpProcessor' },
    variables: {
      idSs58,
      minTimestamp: last24hStartDate.toISOString(),
      maxTimestamp: last24hEndDate.toISOString(),
      groupBy: 'TIMESTAMP_TRUNCATED_TO_HOUR',
    },
    refetchInterval: 30_000,
  });

  const delta24h = useMemo(() => {
    if (!last24hSnapshot) return undefined;

    return (
      Number(currentSupplied) -
      Number(last24hSnapshot?.account?.snapshots.agg?.[0]?.max?.lendingLiquidityUsd ?? 0)
    );
  }, [last24hSnapshot, currentSupplied]);

  return !isLoading && !data?.account?.snapshots.agg?.length ? (
    <EmptyPlaceholder />
  ) : (
    <div className="flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-2">
        <FancyDollarDisplay
          value={formatUsdValue(currentSupplied)}
          wholeClassName="text-24"
          decimalClassName="text-18"
        />
        <div className="flex gap-1 text-12">
          <div className="flex items-center justify-center">
            {delta24h === undefined ? (
              <SkeletonLine width={45} height={12} />
            ) : (
              <>
                {delta24h < 0 ? (
                  <TrendingDown className="text-cf-red-2" />
                ) : (
                  <TrendingUp className="text-cf-green-3" />
                )}
                <span className={delta24h < 0 ? 'text-cf-red-2' : 'text-cf-green-3'}>
                  {delta24h > 0 && '+'}
                  {formatUsdValue(delta24h, undefined, true)}
                </span>
              </>
            )}
          </div>
          <span className="text-cf-light-2">Last 24H</span>
          <QuestionMarkTooltip content="Supply position change over the last 24 hours." />
        </div>
      </div>
      <AreaLineChart
        isLoading={isLoading}
        isDaily={isDaily}
        chartData={
          data?.account?.snapshots.agg
            ?.map((item) => {
              const key = item.keys?.at(0);
              const value = item?.max?.lendingLiquidityUsd;
              return value == null || key == null
                ? null
                : {
                    valueUsd: value,
                    date: key,
                  };
            })
            .filter(isNotNullish) ?? []
        }
      />
    </div>
  );
};
